"use client";

import { c as _c } from "react/compiler-runtime";
import { useServices } from "../services-context";
import { ServiceCard, ServiceCardProps } from "@/app/[tenant]/self-schedule/components/self-schedule-card";
import { Badge } from "@/components/ui";
import { useSelfScheduleContext } from "@/contexts/self-schedule-provider";
import { usePatientVerification } from "@/hooks/self-schedule/use-patient-verification";
import { formatDistance } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";
import { PublicLocationDetail } from "@/types/location";
import { PublicServiceDetail } from "@/types/service";
import { EmptyServicesState } from "./empty-services-state";
import { PromotionCategoryBadge, getPromotionCategoryAccordionHeaderClassName, getPromotionCategoryAccordionItemClassName, hasPromotionServices, isPromotionCategoryName } from "./promotion-category";
import { ServiceAccordion } from "./service-accordion";
export interface RenderBucket {
  id: string;
  name?: string;
  services: PublicServiceDetail[];
  /** 0 for top-level subcategory buckets, >0 for nested. Drives indented rendering
   *  in the location-grouped layout. */
  depth?: number;
  /** Name of the parent category (undefined for root subcategories). */
  parentCategoryName?: string | null;
}
export interface ServiceGroup {
  id: string;
  title: string;
  distance?: number;
  location?: PublicLocationDetail;
  services?: PublicServiceDetail[];
  buckets?: RenderBucket[];
}
interface GroupedServicesProps {
  groups: ServiceGroup[];
  userLocation: {
    lat: number;
    lng: number;
  } | null;
  isServiceSelected: (serviceId: string) => boolean;
  getServiceInstanceCount?: (serviceId: string) => number;
  isServiceUnavailable?: (serviceId: string) => boolean;
  canSelectMore: boolean;
  selectedCount: number;
  maxQuantity: number;
  onServiceClick: (serviceId: string) => void;
  onQuantityChange: (serviceId: string, quantity: number) => void;
  onInfoClick: ServiceCardProps["onInfoClick"];
  subgroupByCategory?: boolean;
}
const cardGridClassName = "grid grid-cols-2 gap-3 md:grid-cols-3";
export const GroupedServices = t0 => {
  const $ = _c(30);
  const {
    groups,
    userLocation,
    isServiceSelected,
    getServiceInstanceCount,
    isServiceUnavailable,
    canSelectMore,
    selectedCount,
    maxQuantity,
    onServiceClick,
    onQuantityChange,
    onInfoClick,
    subgroupByCategory
  } = t0;
  const {
    shouldExposeLocationTypeChoice
  } = useSelfScheduleContext();
  const {
    blockingAppointment
  } = usePatientVerification();
  const showDuration = useServices().state.data?.showServiceDuration !== false;
  let t1;
  if ($[0] !== subgroupByCategory) {
    t1 = group => {
      if (subgroupByCategory && group.buckets) {
        return group.buckets.reduce(_temp, 0);
      }
      return group.services?.length ?? 0;
    };
    $[0] = subgroupByCategory;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const getServiceCount = t1;
  const groupHasPromotionServices = _temp3;
  if (groups.length === 0) {
    let t2;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <EmptyServicesState message="No services available for the selected location type." className="mt-6" />;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  let t2;
  if ($[3] !== blockingAppointment || $[4] !== canSelectMore || $[5] !== isServiceSelected || $[6] !== isServiceUnavailable) {
    t2 = service => !!blockingAppointment || !isServiceSelected(service.id) && !service.onlyBookableOverThePhone && (!canSelectMore || !!isServiceUnavailable?.(service.id));
    $[3] = blockingAppointment;
    $[4] = canSelectMore;
    $[5] = isServiceSelected;
    $[6] = isServiceUnavailable;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const isServiceDisabled = t2;
  let t3;
  if ($[8] !== getServiceInstanceCount || $[9] !== isServiceDisabled || $[10] !== isServiceSelected || $[11] !== maxQuantity || $[12] !== onInfoClick || $[13] !== onQuantityChange || $[14] !== onServiceClick || $[15] !== selectedCount || $[16] !== showDuration) {
    t3 = (service_0, groupId, index, className) => {
      const isSelected = isServiceSelected(service_0.id);
      const selectionCount = getServiceInstanceCount?.(service_0.id) ?? 0;
      const remaining = Math.max(0, maxQuantity - selectedCount);
      const serviceMaxQuantity = service_0.allowMultipleBookings ? selectionCount + remaining : selectionCount;
      return <ServiceCard key={service_0.id + groupId} service={service_0} isSelected={isSelected} selectionCount={selectionCount} disabled={isServiceDisabled(service_0)} maxQuantity={serviceMaxQuantity} onServiceClick={onServiceClick} onQuantityChange={onQuantityChange} onInfoClick={onInfoClick} showDuration={showDuration} animationDelay={index * 25 + 50} className={className} />;
    };
    $[8] = getServiceInstanceCount;
    $[9] = isServiceDisabled;
    $[10] = isServiceSelected;
    $[11] = maxQuantity;
    $[12] = onInfoClick;
    $[13] = onQuantityChange;
    $[14] = onServiceClick;
    $[15] = selectedCount;
    $[16] = showDuration;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  const renderServiceCard = t3;
  let t4;
  if ($[18] !== getServiceCount || $[19] !== shouldExposeLocationTypeChoice || $[20] !== userLocation) {
    t4 = (group_3, groupIndex) => {
      const serviceCount = getServiceCount(group_3);
      const hasPromotion = groupHasPromotionServices(group_3);
      return <div className="flex items-center justify-between w-full"><div className="flex items-center gap-3"><h3 className={cx("text-h4 text-left", isPromotionCategoryName(group_3.id) ? "text-foreground" : "text-primary-foreground")}>{group_3.title}</h3>{hasPromotion && <PromotionCategoryBadge />}{userLocation && group_3.distance !== undefined && group_3.distance !== Infinity && shouldExposeLocationTypeChoice && <>{groupIndex === 0 && <Badge variant="default" className="inline-block">Your Closest Clinic</Badge>}<span className="text-body text-primary-foreground/70">{formatDistance(group_3.distance)}</span></>}<span className={cx("text-body", isPromotionCategoryName(group_3.id) ? "text-foreground-secondary" : "text-primary-foreground/70")}>{`${serviceCount} ${serviceCount === 1 ? "service" : "services"}`}</span></div></div>;
    };
    $[18] = getServiceCount;
    $[19] = shouldExposeLocationTypeChoice;
    $[20] = userLocation;
    $[21] = t4;
  } else {
    t4 = $[21];
  }
  let t5;
  if ($[22] !== renderServiceCard || $[23] !== subgroupByCategory) {
    t5 = group_4 => <div className="p-4">{subgroupByCategory && group_4.buckets ? <div className="space-y-6">{group_4.buckets.map(bucket_1 => {
          const depth = bucket_1.depth ?? 0;
          return <div key={bucket_1.id} style={{
            paddingLeft: `${depth * 16}px`
          }}>{bucket_1.name && <div className="mb-3 flex items-center gap-2 text-body font-medium text-foreground-secondary">{depth > 0 && <span aria-hidden="true">└</span>}<span>{bucket_1.name}</span>{bucket_1.parentCategoryName && depth > 0 && <span className="text-caption text-foreground-muted">in {bucket_1.parentCategoryName}</span>}{hasPromotionServices(bucket_1.services) && <PromotionCategoryBadge />}</div>}<div className={cardGridClassName}>{bucket_1.services.map((service_1, index_0) => renderServiceCard(service_1, group_4.id, index_0, "!border-transparent"))}</div></div>;
        })}</div> : <div className={cardGridClassName}>{group_4.services?.map((service_2, index_1) => renderServiceCard(service_2, group_4.id, index_1))}</div>}</div>;
    $[22] = renderServiceCard;
    $[23] = subgroupByCategory;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  let t6;
  if ($[25] !== getServiceCount || $[26] !== groups || $[27] !== t4 || $[28] !== t5) {
    t6 = <div className="mt-4"><ServiceAccordion groups={groups} getServiceCount={getServiceCount} getItemClassName={_temp4} getHeaderClassName={_temp5} renderGroupHeader={t4} renderGroupBody={t5} /></div>;
    $[25] = getServiceCount;
    $[26] = groups;
    $[27] = t4;
    $[28] = t5;
    $[29] = t6;
  } else {
    t6 = $[29];
  }
  return t6;
};
export default GroupedServices;
function _temp(acc, bucket) {
  return acc + bucket.services.length;
}
function _temp2(bucket_0) {
  return hasPromotionServices(bucket_0.services);
}
function _temp3(group_0) {
  return hasPromotionServices(group_0.services) || group_0.buckets?.some(_temp2) || false;
}
function _temp4(group_1) {
  return getPromotionCategoryAccordionItemClassName(group_1.id);
}
function _temp5(group_2) {
  return getPromotionCategoryAccordionHeaderClassName(group_2.id);
}